import React, { useState, useEffect, useCallback } from "react";
import "./HenrriDevisModal.css";

const API_BASE = "https://kanban.amservices.eu/api";

/**
 * Modale listant les devis Henrri VALIDÉS non encore traités.
 *
 * Props :
 *   onClose  : () => void            — ferme la modale
 *   onImport : (devis) => void       — clic "Importer" : le parent ouvre la
 *                                      modale de création pré-remplie
 *   onCountChange : (n) => void      — (optionnel) remonte le nb à traiter
 */
const DevisValidesModal = ({ onClose, onImport, onCountChange }) => {
    const [devis, setDevis] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [closing, setClosing] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const fetchListe = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/get_devis_valides_a_traiter.php`);
            const data = await res.json();
            if (data.success) {
                setDevis(data.data);
                if (typeof onCountChange === "function") onCountChange(data.data.length);
            } else {
                setError(data.error || "Erreur lors du chargement des devis validés.");
            }
        } catch (err) {
            console.error(err);
            setError("Impossible de contacter le serveur.");
        } finally {
            setLoading(false);
        }
    }, [onCountChange]);

    useEffect(() => { fetchListe(); }, [fetchListe]);

    const handleClose = () => {
        setClosing(true);
        setTimeout(() => onClose(), 280);
    };

    const marquerTraite = async (d, action) => {
        setBusyId(d.id);
        try {
            await fetch(`${API_BASE}/marquer_devis_traite.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    henrri_document_id: d.id,
                    numero_devis: d.numero,
                    action,
                }),
            });
            setDevis((prev) => {
                const next = prev.filter((x) => x.id !== d.id);
                if (typeof onCountChange === "function") onCountChange(next.length);
                return next;
            });
        } catch (err) {
            console.error(err);
            alert("Erreur lors du traitement du devis.");
        } finally {
            setBusyId(null);
        }
    };

    const handleImport = async (d) => {
        await marquerTraite(d, "importe");
        // Ferme la modale et demande au parent d'ouvrir la création pré-remplie
        setClosing(true);
        setTimeout(() => onImport(d), 200);
    };

    const handleIgnore = (d) => {
        if (!window.confirm(`Ignorer le devis ${d.numero || ""} ? Il ne réapparaîtra plus dans cette liste.`)) return;
        marquerTraite(d, "ignore");
    };

    const fmtMontant = (v) =>
        v == null ? "—" : parseFloat(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

    const filteredDevis = devis.filter((d) => {
        const low = searchTerm.trim().toLowerCase();
        if (!low) return true;
        return (
            (d.numero || "").toLowerCase().includes(low) ||
            (d.client || "").toLowerCase().includes(low) ||
            (d.titre || "").toLowerCase().includes(low)
        );
    });

    return (
        <div className={`modal-overlay ${closing ? "closing" : ""}`}>
            <div className="modal-content henrri-modal">
                <div className="henrri-modal-header">
                    <h2>Devis finalisés à traiter</h2>
                    <span className="henrri-count">{filteredDevis.length}/{devis.length} à traiter</span>
                </div>

                <div className="henrri-search-row">
                    <input
                        type="text"
                        className="henrri-search-input"
                        placeholder="Rechercher par client, numéro ou objet..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="henrri-table-wrapper">
                    {loading ? (
                        <p className="henrri-state">Chargement…</p>
                    ) : error ? (
                        <p className="henrri-state henrri-error">⚠️ {error}</p>
                    ) : devis.length === 0 ? (
                        <p className="henrri-state">Aucun devis finalisé en attente.</p>
                    ) : filteredDevis.length === 0 ? (
                        <p className="henrri-state">Aucun devis ne correspond à la recherche.</p>
                    ) : (
                        <table className="henrri-table">
                            <thead>
                                <tr>
                                    <th>N°</th>
                                    <th>Client</th>
                                    <th>Modifié le</th>
                                    <th className="ta-right">Montant HT</th>
                                    <th className="ta-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDevis.map((d) => (
                                    <tr key={d.id}>
                                        <td className="mono">{d.numero || "—"}</td>
                                        <td>
                                            {d.client || <em>Sans client</em>}
                                            {d.titre && <div className="henrri-titre">{d.titre}</div>}
                                        </td>
                                        <td>{fmtDate(d.date_validation || d.date)}</td>
                                        <td className="ta-right strong">{fmtMontant(d.montant_ht)}</td>
                                        <td className="ta-center">
                                            <div className="devis-valide-actions">
                                                <button
                                                    type="button"
                                                    className="btn-import-devis"
                                                    disabled={busyId === d.id}
                                                    onClick={() => handleImport(d)}
                                                >Importer</button>
                                                <button
                                                    type="button"
                                                    className="btn-ignore-devis"
                                                    disabled={busyId === d.id}
                                                    onClick={() => handleIgnore(d)}
                                                    title="Marquer comme traité sans créer de carte"
                                                >Ignorer</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="modal-btns">
                    <button type="button" onClick={fetchListe} className="btn-add">↻ Rafraîchir</button>
                    <button type="button" onClick={handleClose}>Fermer</button>
                </div>
            </div>
        </div>
    );
};

export default DevisValidesModal;